import { clsx } from 'clsx';
import { Flex } from '@nula/core-ui';
import styles from './step-progress.module.css';

export interface StepProgressProps {
  /** Total number of steps. */
  total: number;
  /** Zero-based index of the current step. */
  current: number;
  /**
   * When provided, completed steps (index < current) become clickable so the
   * user can jump back. Current and upcoming steps are never clickable.
   */
  onStepClick?: (index: number) => void;
}

/**
 * Thin progress bars shown above each onboarding step — completed steps are
 * solid primary, the current step is a dimmed primary, upcoming steps are
 * muted. Matches the onboarding mockup's minimal step indicator.
 *
 * Completed bars are rendered as buttons (when onStepClick is given) so the
 * user can navigate back to an earlier step.
 */
export function StepProgress({ total, current, onStepClick }: StepProgressProps) {
  return (
    // Not aria-hidden when interactive: the back-navigation buttons must remain
    // reachable by assistive tech.
    <Flex gap="xs" className={styles.root} aria-hidden={onStepClick ? undefined : true}>
      {Array.from({ length: total }, (_, i) => {
        const done = i < current;
        const className = clsx(
          styles.bar,
          done && styles.done,
          i === current && styles.current,
        );
        if (onStepClick && done) {
          return (
            <button
              key={i}
              type="button"
              className={clsx(className, styles.clickable)}
              onClick={() => onStepClick(i)}
              aria-label={`Go to step ${i + 1}`}
            />
          );
        }
        return <span key={i} className={className} />;
      })}
    </Flex>
  );
}
